import { Sort, Search, Spinner, Button, SortOrder, Progress, Filter } from '@cimplico/components';
import { useEffect, useMemo, useState } from 'react';
import { useAllFYIEntities, useFYIEntities } from '../../../../../../../queries/use-fyi-entities';
import { FYIEntity } from '../../../../../../../types/fyi-docs';
import { CreateFYIEntitiesResponse } from '../../../../../../../queries/use-create-fyi-entities';
import { useSearchParams } from 'react-router-dom';
import EmptyStateNew from '../../../../../../../components/empty-state/EmptyStateNew';
import FYIEntitiesList from './FYIEntitiesList';
import FYIEntitiesOnboardAlert from './FYIEntitiesOnboardAlert';
import { SquaresPlusIcon } from '@heroicons/react/24/outline';
import { UserRoleEnum } from '../../../../../../../types/user';
import { useUserFirmRoleContext } from '../../../../../../../providers/UserFirmRoleProvider';
import { useFirmContext } from '../../../../../../../providers/FirmProvider';
import { useFYIGroups } from '../../../../../../../queries/use-fyi-groups';

export type OnboardingFormEntry = {
    fyiEntity: FYIEntity;
    isSelected: boolean;
    month?: number | null;
    day?: number | null;
    monthError?: string;
    dayError?: string;
};

export type OnboardingFormData = {
    data: { groupName: string; entities: OnboardingFormEntry[] }[];
    globalSelected: boolean;
};

type FYIEntitiesOnboardingProps = {
    fullPage?: boolean;
};

type GroupedEntities = {
    groupName: string;
    entities: FYIEntity[];
};

function FYIEntitiesOnboarding({ fullPage }: FYIEntitiesOnboardingProps): JSX.Element {
    const [search, setSearch] = useState<string>();
    const [hideOnboarded, setHideOnboarded] = useState(true);
    const [sort, setSort] = useState<{ order: SortOrder; option?: string | undefined }>({ order: 'asc', option: 'name' });
    const [successAlert, setSuccessAlert] = useState<CreateFYIEntitiesResponse | undefined>(undefined);
    const [errorAlert, setErrorAlert] = useState<{ [entityName: string]: string[] } | undefined>(undefined);

    const [groupedEntities, setGroupedEntities] = useState();

    const [params] = useSearchParams();
    const defaultFYIGroup = params.get('groupId');
    const defaultFYIGroupName = params.get('groupName');

    const role = useUserFirmRoleContext();

    const { integrations } = useFirmContext();

    const groupsQuery = useFYIGroups({}, Boolean(integrations.fyi?.cabinet));

    const { isError, isSuccess, isLoading, refetch, isFetching, data, fetchNextPage, isFetchingNextPage } = useAllFYIEntities(
        {
            group: defaultFYIGroup ? [Number(defaultFYIGroup)] : undefined,
            // showUnmappedEntities: Boolean(defaultFYIGroup),
            page: 1,
            limit: 500,
        },
        Boolean(groupsQuery.data)
    );

    const fyiEntities = useMemo(
        () =>
            data?.pages?.reduce<FYIEntity[]>(
                (acc, p) => [
                    ...acc,
                    ...p.entities.map((e) => {
                        const matchingGroupName = groupsQuery.data?.find((g) => g.id === e.entityGroupId)?.name;
                        return { ...e, group: { ...e.group, name: matchingGroupName || '' } } as FYIEntity;
                    }),
                ],
                []
            ) ?? [],
        [data?.pages, groupsQuery.data]
    );
    const total = data?.pages?.at(0)?.total;

    useEffect(() => {
        if (total !== undefined && fyiEntities.length < total) {
            fetchNextPage();
        }
    }, [fetchNextPage, fyiEntities.length, total]);

    if (!integrations.fyi?.cabinet) {
        return (
            <div className="my-8">
                <EmptyStateNew
                    title="Configure FYI"
                    titleSize="small"
                    icon={<SquaresPlusIcon />}
                    helpText={
                        role === UserRoleEnum.ADMIN
                            ? 'Please go to the configuration tab to setup your FYI integration'
                            : 'Please get your firm admin to setup your FYI integration'
                    }
                />
            </div>
        );
    }
    if (isLoading || groupsQuery.isLoading) {
        return (
            <div className="w-fit mx-auto my-16">
                <Spinner size="lg" />
            </div>
        );
    }
    if (isFetchingNextPage || (fyiEntities.length !== total && total !== undefined)) {
        return (
            <div className="flex-1 py-16">
                {total !== undefined && (
                    <div className="mx-auto w-fit flex flex-col items-center gap-2">
                        {/* <Spinner /> */}
                        <Progress type="circle" percent={(fyiEntities.length / total) * 100} />
                        <p className="text-sm text-gray-800">
                            Loading {fyiEntities.length} out of <strong>{total}</strong> entities...
                        </p>
                    </div>
                )}
            </div>
        );
    }

    if (isError || !isSuccess) {
        return (
            <div className="flex justify-center flex-1 py-16">
                <div className="mx-auto w-fit flex flex-col items-center">
                    <p className="text-sm text-gray-600 mb-2">Unable to load FYI entities.</p>
                    <Button
                        type="secondary"
                        size="sm"
                        label={isFetching ? 'Retrying...' : 'Retry'}
                        disabled={isFetching}
                        onClick={refetch}
                        isLoading={isFetching}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="pt-4 min-h-[600px]">
            <div className="flex items-center gap-2 mb-2">
                <Filter
                    onChange={({ currentFilterState }): void => {
                        if (currentFilterState[0].type === 'toggle') {
                            setHideOnboarded(currentFilterState[0].option.active);
                        }
                    }}
                    filters={[{ title: 'Hide Onboarded', type: 'toggle', id: 'onboarded', option: { active: hideOnboarded } }]}
                />
                <span className="flex-1">
                    <Search defaultValue={search} onChange={setSearch} placeholder="Search..." />
                </span>
                <Sort
                    defaultSortOption={sort?.option}
                    defaultSortOrder={sort?.order}
                    onChange={({ currentSortState }): void => setSort({ ...currentSortState })}
                    sortOptions={[{ id: 'name', label: 'Name' }]}
                />
            </div>

            {(successAlert || errorAlert) && (
                <FYIEntitiesOnboardAlert
                    errors={errorAlert}
                    success={successAlert}
                    onSuccessAlertClose={(): void => setSuccessAlert(undefined)}
                    onErrorAlertClose={(): void => setErrorAlert(undefined)}
                />
            )}
            {(!fyiEntities || fyiEntities.length === 0) && !isLoading && (
                <>
                    {fullPage && defaultFYIGroupName && (
                        <p className="text-sm font-medium border-b pt-4 pb-2">{decodeURIComponent(defaultFYIGroupName)}</p>
                    )}
                    <div className="mt-12">
                        <EmptyStateNew
                            title="No available FYI entities"
                            titleSize="small"
                            helpText="Onboard entities from FYI to allow for automatic mapping between Cimplico Workpapers and FYI."
                            helpLink="https://support.cimplico.com/support/solutions/articles/51000329410-integration-with-fyi"
                        />
                    </div>
                </>
            )}
            {fyiEntities && fyiEntities.length > 0 && (
                <FYIEntitiesList
                    showOnboarded={!hideOnboarded}
                    fyiEntities={fyiEntities}
                    fullPage={fullPage}
                    search={search}
                    sort={sort}
                    onSetAlert={(alert): void => {
                        if (alert) {
                            setSuccessAlert(alert.success);
                            setErrorAlert(alert.errors);
                        } else {
                            setSuccessAlert(undefined);
                            setErrorAlert(undefined);
                        }
                    }}
                    onClearSearch={(): void => setSearch('')}
                />
            )}
        </div>
    );
}

export default FYIEntitiesOnboarding;
